// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';

// Import dom-ready
import domReady from '@wordpress/dom-ready';

// Import api-fetch
import apiFetch from '@wordpress/api-fetch';

// Import styles
import './style.scss';

// Add the nonce as middleware. This automatically adds the `X-WP-Nonce` header to requests.
apiFetch.use( apiFetch.createNonceMiddleware(
	UncannyAutomatorBackend.rest.nonce
) );

/**
 * Handles the functionality of the banner that requests reviews
 */
class ReviewBanner {
	// eslint-disable-next-line require-jsdoc
	constructor() {
		if ( this.hasBanner 
			&& isDefined( this.$firstStepButtons.positive ) 
			&& isDefined( this.$firstStepButtons.negative ) 
		) {
			// Listen first step
			this.listenFirstStep();

			// Listen the buttons that close the banners
			this.listenCloseButtons();
		}
	}

	/**
	 * Listens the buttons defined on the first step
	 */
	listenFirstStep() {
		this.$firstStepButtons.positive.addEventListener( 'click', () => {
			this.showSecondStep( 'positive' );
		} );

		this.$firstStepButtons.negative.addEventListener( 'click', () => {
			this.showSecondStep( 'negative' );
		} );
	}

	/**
	 * Listens the buttons that close the banners
	 */
	listenCloseButtons() {
		// Do the same for all the three banners
		[ this.$banner, ...Object.values( this.$secondStepBanners ) ].forEach( ( $banner ) => {
			// Bail if the banner is not defined ( credit notices )
			if ( ! isDefined( $banner ) ) {
				return;
			}
			// Find the buttons
			$banner.querySelectorAll( '[data-action="hide-banner-on-click"]' ).forEach( ( $button ) => {
				// Listen clicks to the button
				$button.addEventListener( 'click', () => {
					// Check if we have a track data attribute.
					if ( isDefined( $button.dataset.track ) ) {
						this.trackReviewData( $button.dataset.track );
					}
					// Hide the banner.
					$button.closest( '.notice' ).style.display = 'none';
				} );
			} );
		} );
	}

	/**
	 * Shows the second step of the review banner
	 *
	 * @param {String} bannerKey - The bannerKey to show
	 *
	 * @return {void}
	 */
	showSecondStep( bannerKey ) {
		// Bail if the banner is not defined ( credit notices )
		if ( ! isDefined( this.$secondStepBanners[ bannerKey ] ) ) {
			return;
		}

		// Wait 150ms to improve UX
		// That way things won't happen too fast and the user will have time to process what's happening
		setTimeout( () => {
			// Show the banner
			this.$secondStepBanners[ bannerKey ].style.display = 'block';
		}, 150 );

	}

	/**
	 * Sends the tracking data to the server
	 *
	 * @param trackEvent
	 * @return {void}
	 */
	trackReviewData( trackEvent ) {

		// Bail if the tracking data is not defined ( credit notices )
		if ( ! this.bannerKey || ! trackEvent ) {
			return;
		}

		// Send the tracking data
		apiFetch( {
			url: `${UncannyAutomatorBackend.rest.url}/track-review/`,
			method: 'POST',
			data: {
				banner:this.bannerKey,
				event:trackEvent,
			}
		} ).catch( ( error ) => {
			console.error( 'Error sending review data', error );
		} );
	}

	/**
	 * Returns the banners displayed in the second step
	 *
	 * @return {Object} Object of Nodes
	 */
	get $secondStepBanners() {
		return {
			positive: document.getElementById( 'uap-review-banner-positive' ),
			negative: document.getElementById( 'uap-review-banner-negative' ),
		}
	}

	/**
	 * Returns the buttons that defines the next step
	 *
	 * @return {Object} Object of Nodes
	 */
	get $firstStepButtons() {
		return {
			positive: document.getElementById( 'uap-review-banner-btn-positive' ),
			negative: document.getElementById( 'uap-review-banner-btn-negative' ),
		}
	}

	/**
	 * Checks whether the page contains the review banner
	 *
	 * @return {Boolean} True if the banner exists on the page
	 */
	get hasBanner() {
		return isDefined( this.$banner );
	}

	/**
	 * Returns the review banner
	 *
	 * @return {Node} The banner
	 */
	get $banner() {
		return document.getElementById( 'uap-review-banner' );
	}

	/**
	 * Returns the banner key
	 *
	 * @return {String|Boolean} The banner key or false if not defined
	 */
	get bannerKey() {
		return isDefined( this.$banner ) && isDefined( this.$banner.dataset.banner )
			? this.$banner.dataset.banner
			: false;
	}
}

// Invoke classes when the DOM is ready
domReady( () => {
	new ReviewBanner();
} );